import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app-language';
  private readonly DEFAULT_LANGUAGE = 'fr';

  // Langues disponibles
  private readonly availableLanguages: Language[] = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  private currentLanguageSubject: BehaviorSubject<string>;
  public currentLanguage$: Observable<string>;

  constructor(private translate: TranslateService) {
    // Initialiser avec la langue sauvegardée ou la langue par défaut
    const savedLang = this.getSavedLanguage();
    this.currentLanguageSubject = new BehaviorSubject<string>(savedLang);
    this.currentLanguage$ = this.currentLanguageSubject.asObservable();

    // Définir la langue par défaut
    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE);
    this.translate.use(savedLang);
  }

  /**
   * Récupère la langue sauvegardée dans localStorage
   */
  private getSavedLanguage(): string {
    return localStorage.getItem(this.STORAGE_KEY) || this.DEFAULT_LANGUAGE;
  }

  /**
   * Change la langue de l'application
   * @param langCode Code de langue (ex: 'fr', 'en', 'fr-FR', 'en-US')
   */
  setLanguage(langCode: string): void {
    // Normaliser le code de langue
    const normalizedLang = this.normalizeLanguageCode(langCode);

    // Vérifier si la langue est supportée
    if (!this.isLanguageSupported(normalizedLang)) {
      console.warn(`Language '${langCode}' is not supported. Falling back to default.`);
      return;
    }

    // Appliquer la langue
    this.translate.use(normalizedLang);

    // Sauvegarder dans localStorage
    localStorage.setItem(this.STORAGE_KEY, normalizedLang);

    // Notifier les observateurs
    this.currentLanguageSubject.next(normalizedLang);

    console.log(`Language changed to: ${normalizedLang}`);
  }

  /**
   * Récupère la langue actuelle
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Récupère la liste des langues disponibles
   */
  getAvailableLanguages(): Language[] {
    return this.availableLanguages;
  }

  /**
   * Normalise le code de langue (ex: 'fr-FR' -> 'fr')
   */
  private normalizeLanguageCode(langCode: string): string {
    return langCode.toLowerCase().split('-')[0];
  }

  /**
   * Vérifie si une langue est supportée
   */
  private isLanguageSupported(langCode: string): boolean {
    return this.availableLanguages.some(lang => lang.code === langCode);
  }

  /**
   * Récupère les informations d'une langue par son code
   */
  getLanguageInfo(langCode: string): Language | undefined {
    const normalizedCode = this.normalizeLanguageCode(langCode);
    return this.availableLanguages.find(lang => lang.code === normalizedCode);
  }
}
